import { DateTime, Duration } from "luxon";
import type { FC } from "react";
import React, { useEffect, useState } from "react";

function formatDateFromNow(date: Date, now: Date): string {
  const dateTime = DateTime.fromJSDate(date).setLocale("en-US");

  const nowDateTime = DateTime.fromJSDate(now);

  const { minutes } = nowDateTime.diff(dateTime, "minutes");
  const minutesAgo = Math.trunc(minutes);

  if (minutesAgo < 1) {
    return "Just now";
  }

  const RECENT_AGE_THRESHOLD = Duration.fromObject({ minutes: 30 });

  if (minutesAgo <= RECENT_AGE_THRESHOLD.as("minutes")) {
    return `${minutesAgo} minute(s) ago`;
  }

  if (dateTime.hasSame(nowDateTime, "day")) {
    return dateTime.toFormat("h:mm a");
  }

  if (dateTime.hasSame(nowDateTime.minus({ days: 1 }), "day")) {
    return `Yesterday ${dateTime.toFormat("h:mm a")}`;
  }

  return dateTime.toFormat("dd MMM yyyy h:mm a");
}

function subscribe(subscriber: () => void): () => void {
  subscribers.add(subscriber);

  return () => {
    subscribers.delete(subscriber);
  };
}

const REFRESH_INTERVAL = Duration.fromObject({ seconds: 1 });

let now = new Date();

const subscribers = new Set<() => void>();

setInterval(() => {
  now = new Date();

  for (const subscriber of subscribers) {
    subscriber();
  }
}, REFRESH_INTERVAL.toMillis());

interface DateFromNowProps {
  readonly date: string;
}

export const DateFromNow: FC<DateFromNowProps> = ({ date }) => {
  const [formattedDate, setFormattedDate] = useState(() => {
    const dateObj = new Date(date);

    return formatDateFromNow(dateObj, now);
  });

  useEffect(() => {
    const dateObj = new Date(date);

    return subscribe(() => {
      setFormattedDate(formatDateFromNow(dateObj, now));
    });
  }, [date]);

  return <>{formattedDate}</>;
};
